/**
 * Centralized environment configuration module.
 * All environment variable access should go through this module.
 *
 * This ensures:
 * - Type safety for all config values
 * - Clear error messages when required vars are missing
 * - Single source of truth for environment configuration
 *
 * Note: Server config uses getters to defer validation until runtime,
 * allowing the build to complete even when env vars aren't set.
 */

// ============================================================================
// Server-side only environment variables
// These should NOT be exposed to the client
// ============================================================================

/**
 * Validates and returns a required server-side environment variable.
 * Throws an error with a helpful message if the variable is not set.
 */
function requireServerEnv(name: string): string {
  const value = process.env[name];
  if (!value) {
    throw new Error(
      `Missing required server environment variable: ${name}. ` +
      `Please set it in your .env.local file. See .env.example for reference.`
    );
  }
  return value;
}

/**
 * Returns a server-side environment variable or undefined if not set.
 */
function optionalServerEnv(name: string): string | undefined {
  return process.env[name];
}

// ============================================================================
// Server Configuration (only accessible in Server Components / API routes)
// Uses getters to defer validation until runtime (not build time)
// ============================================================================

export const serverConfig = {
  /**
   * Resend API key for sending emails.
   * Required for contact form and newsletter functionality.
   */
  get resendApiKey(): string {
    return requireServerEnv('RESEND_API_KEY');
  },

  /**
   * Cloudflare Turnstile secret key for validating captcha on server.
   * Required for contact form spam protection.
   */
  get turnstileSecretKey(): string {
    return requireServerEnv('TURNSTILE_SECRET_KEY');
  },

  /**
   * Email address to receive contact form submissions.
   * Required for contact form functionality.
   */
  get contactEmail(): string {
    return requireServerEnv('CONTACT_EMAIL');
  },

  /**
   * Resend Audience ID for newsletter subscriptions.
   * Optional - newsletter will gracefully degrade if not set.
   */
  get resendAudienceId(): string | undefined {
    return optionalServerEnv('RESEND_AUDIENCE_ID');
  },

  /**
   * Sentry auth token for source map uploads.
   * Optional - only needed for production builds with source maps.
   */
  get sentryAuthToken(): string | undefined {
    return optionalServerEnv('SENTRY_AUTH_TOKEN');
  },

  /**
   * Sentry organization slug.
   * Optional - only needed for production builds with source maps.
   */
  get sentryOrg(): string | undefined {
    return optionalServerEnv('SENTRY_ORG');
  },

  /**
   * Sentry project slug.
   * Optional - only needed for production builds with source maps.
   */
  get sentryProject(): string | undefined {
    return optionalServerEnv('SENTRY_PROJECT');
  },
};

// ============================================================================
// Public Configuration (accessible in both client and server)
// These are evaluated at module load time since they're public/optional
// ============================================================================

export const publicConfig = {
  /**
   * Google Analytics 4 Measurement ID.
   * Optional - analytics will be disabled if not set.
   */
  ga4Id: process.env.NEXT_PUBLIC_GA4_ID,

  /**
   * Cloudflare Turnstile site key for rendering captcha widget.
   * Has a default fallback for development.
   */
  turnstileSiteKey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '0x4AAAAAACNdSGkUZqWDtzRl',

  /**
   * Sentry DSN for error tracking.
   * Optional - error tracking will be disabled if not set.
   */
  sentryDsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  /**
   * Whether the app is running in development mode.
   */
  isDevelopment: process.env.NODE_ENV === 'development',

  /**
   * Whether the app is running in production mode.
   */
  isProduction: process.env.NODE_ENV === 'production',

  /**
   * Current Node environment.
   */
  nodeEnv: process.env.NODE_ENV,
};

// ============================================================================
// Type exports
// ============================================================================

export type ServerConfig = typeof serverConfig;
export type PublicConfig = typeof publicConfig;
